import React, { useEffect, useState } from 'react'
import { Chip, CircularProgress, Modal, ModalBody, ModalContent, Spacer, Tab, Tabs } from '@heroui/react'
import { billing } from '@/services/billing/billing-service'
import { frequencies, FrequencyEnum, Product } from '@interfaces/pricing'
import PricingCard from './pricing-card'
import EmbeddedCheckout from './embedded-checkout'
import toast from 'react-hot-toast'
import { AnalyticsService } from '@/services/analytics'
import useAppState from '@/contexts/state'
import { AuthState } from '@/contexts/slices/auth'
import { SubscriptionData, SubscriptionStatus } from '@interfaces/auth'
import { useTemporaryStore } from '@/contexts/temporaryStore'

interface PricingProps {
  onSuccess?: () => void
  ctaText?: string
}

/**
 * Pricing component that displays subscription plans and handles embedded Stripe checkout
 * Shows an embedded checkout modal instead of redirecting to Stripe's hosted page
 */
export default function Pricing({ onSuccess, ctaText }: PricingProps = {}) {
  const product = useTemporaryStore(s => s.product)
  const setProduct = useTemporaryStore(s => s.setProduct)
  const [selectedFrequency, setSelectedFrequency] = React.useState(frequencies[0])
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [showCheckoutModal, setShowCheckoutModal] = useState(false)

  const { setSubscription } = useAppState((state: unknown) => ({
    setSubscription: (state as AuthState).setSubscription,
  }))

  // Fallback: fetch products on-demand if not cached
  useEffect(() => {
    if (!product) {
      billing
        .getProducts()
        .then(({ data }) => {
          setProduct(data.products?.[0])
        })
        .catch(() => {})
    }
  }, [product, setProduct])

  const onFrequencyChange = (selectedKey: React.Key) => {
    const frequencyIndex = frequencies.findIndex(f => f.key === selectedKey)
    setSelectedFrequency(frequencies[frequencyIndex])
  }

  const onSubscribe = async (product: Product) => {
    try {
      setCheckoutLoading(true)
      const { data } = await billing.createSubscription(product.price[selectedFrequency.key].id)
      setClientSecret(data.clientSecret)
      setShowCheckoutModal(true)
    } catch (error) {
      toast.error('Failed to create checkout session')
    } finally {
      setCheckoutLoading(false)
    }
  }

  const handleCheckoutComplete = async () => {
    // Poll the backend to verify subscription was created
    setCheckoutLoading(true)
    const maxAttempts = 10
    let attempts = 0

    const postSubscriptionActions = () => {
      AnalyticsService.trackPurchase()
      setShowCheckoutModal(false)
      setClientSecret(null)
      toast.success('Subscription activated successfully!')
      onSuccess?.()
    }

    const pollSubscription = async (attemptNumber: number) => {
      const response = await billing.checkoutCallback()
      if (response.status === 200) {
        const subscription = response.data as SubscriptionData
        if (subscription && subscription.id) {
          // For first 9 attempts, only accept 'active' status
          if (attemptNumber < maxAttempts) {
            if (subscription.status === SubscriptionStatus.Active) {
              setSubscription(subscription)
              postSubscriptionActions()
              return true
            }
            return false
          } else {
            // On last attempt, accept any valid subscription returned from the backend
            setSubscription(subscription)
            postSubscriptionActions()
            return true
          }
        }
      }
      return false
    }

    // Poll 10 times, every 2 seconds
    const intervalId = setInterval(async () => {
      attempts++
      const success = await pollSubscription(attempts)
      if (success || attempts >= maxAttempts) {
        clearInterval(intervalId)
        setCheckoutLoading(false)
        if (!success) {
          toast.error('Could not verify subscription. Please refresh the page.')
        }
      }
    }, 2000)
  }

  return (
    <div className="relative flex max-w-4xl flex-col items-center justify-center">
      {!product ? (
        <CircularProgress color="success" />
      ) : (
        <>
          <Tabs
            classNames={{
              tabList: 'bg-default-100/70',
              cursor: 'bg-background dark:bg-default-200/30',
              tab: 'data-[hover-unselected=true]:opacity-90',
            }}
            radius="full"
            onSelectionChange={onFrequencyChange}
          >
            <Tab key={FrequencyEnum.Monthly} title="Pay monthly" />
            <Tab
              key={FrequencyEnum.Yearly}
              aria-label="Pay yearly"
              className="pr-0.5"
              title={
                <div className="flex items-center gap-2">
                  <p>Pay yearly</p>
                  <Chip color="primary" variant="flat" style={{ backgroundColor: '#e4ff9e', color: '#000' }}>
                    2 months free
                  </Chip>
                </div>
              }
            />
          </Tabs>
          <Spacer y={4} />
          <PricingCard
            frequency={selectedFrequency}
            onSubscribe={async () => await onSubscribe(product)}
            product={product}
            ctaText={ctaText}
          />
        </>
      )}

      {/* Embedded Checkout Modal */}
      <Modal
        backdrop="blur"
        isOpen={showCheckoutModal}
        onClose={() => !checkoutLoading && setShowCheckoutModal(false)}
        isDismissable={!checkoutLoading}
        className="max-h-[96vh] w-[80vw] max-w-[80rem] overflow-y-auto"
      >
        <ModalContent>
          <ModalBody className="p-0">
            {clientSecret && <EmbeddedCheckout clientSecret={clientSecret} onComplete={handleCheckoutComplete} />}
            {checkoutLoading && (
              <div className="flex min-h-[400px] items-center justify-center">
                <CircularProgress color="success" />
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  )
}
